import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '@/ui/components/atoms/Button';
import type { TrackerChannel, NotificationSettings } from '../types/tracker.types';

interface ChannelNotificationSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  channel: TrackerChannel;
  onSave: (settings: {
    notifications_enabled: boolean;
    notification_settings: NotificationSettings;
  }) => Promise<void>;
}

export const ChannelNotificationSettings: React.FC<ChannelNotificationSettingsProps> = ({
  isOpen,
  onClose,
  channel,
  onSave
}) => {
  // Notification settings state
  const [notificationsEnabled, setNotificationsEnabled] = useState(channel.notifications_enabled);
  const [settings, setSettings] = useState<NotificationSettings>(channel.notification_settings);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    try {
      setIsSaving(true);

      // Save notification settings
      await onSave({
        notifications_enabled: notificationsEnabled,
        notification_settings: settings
      });

      onClose();
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="tracker-modal-backdrop" onClick={onClose} />

      {/* Modal */}
      <div className="tracker-modal">
        <div className="tracker-modal-header">
          <h3 className="tracker-modal-title">
            Paramètres de notification
          </h3>
          <button
            className="tracker-modal-close"
            onClick={onClose}
            aria-label="Fermer"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="tracker-modal-content">
          <div className="tracker-notification-settings">
            {/* Master Toggle */}
            <div className="tracker-setting-item master">
              <div className="tracker-setting-info">
                <h4 className="tracker-setting-title">Notifications</h4>
                <p className="tracker-setting-description">
                  Activer les notifications pour {channel.name}
                  {channel.subscription_source === 'auto_responsable' && (
                    <span className="tracker-auto-subscription-badge">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                      Auto-abonné (responsable)
                    </span>
                  )}
                </p>
              </div>
              <label className="tracker-toggle">
                <input
                  type="checkbox"
                  checked={notificationsEnabled}
                  onChange={(e) => setNotificationsEnabled(e.target.checked)}
                />
                <span className="tracker-toggle-slider"></span>
              </label>
            </div>

            {/* Individual Settings */}
            {notificationsEnabled && (
              <div className="tracker-setting-group">
                <h5 className="tracker-setting-group-title">
                  Types de notifications
                </h5>

                <div className="tracker-setting-item">
                  <div className="tracker-setting-info">
                    <h4 className="tracker-setting-title">Nouvelles tâches</h4>
                    <p className="tracker-setting-description">
                      Notifier lors de la création d'une nouvelle tâche
                    </p>
                  </div>
                  <label className="tracker-toggle">
                    <input
                      type="checkbox"
                      checked={settings.new_tasks}
                      onChange={(e) =>
                        setSettings({ ...settings, new_tasks: e.target.checked })
                      }
                    />
                    <span className="tracker-toggle-slider"></span>
                  </label>
                </div>

                <div className="tracker-setting-item">
                  <div className="tracker-setting-info">
                    <h4 className="tracker-setting-title">Commentaires</h4>
                    <p className="tracker-setting-description">
                      Notifier lors de l'ajout d'un commentaire
                    </p>
                  </div>
                  <label className="tracker-toggle">
                    <input
                      type="checkbox"
                      checked={settings.task_comments}
                      onChange={(e) =>
                        setSettings({ ...settings, task_comments: e.target.checked })
                      }
                    />
                    <span className="tracker-toggle-slider"></span>
                  </label>
                </div>

                <div className="tracker-setting-item">
                  <div className="tracker-setting-info">
                    <h4 className="tracker-setting-title">Changements de statut</h4>
                    <p className="tracker-setting-description">
                      Notifier lors du changement de statut d'une tâche
                    </p>
                  </div>
                  <label className="tracker-toggle">
                    <input
                      type="checkbox"
                      checked={settings.task_status_changes}
                      onChange={(e) =>
                        setSettings({ ...settings, task_status_changes: e.target.checked })
                      }
                    />
                    <span className="tracker-toggle-slider"></span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="tracker-modal-footer">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
          >
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </>
  );
};
